import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Home, ChevronRight, MoreHorizontal } from "lucide-react";
import { useBreadcrumb } from "../providers/BreadcrumbProvider";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Breadcrumb: React.FC = () => {
  const { t } = useTranslation();

  // Context hook - handles all state and navigation
  const { breadcrumbItems, showBreadcrumb, navigateToItem } = useBreadcrumb();

  // Helper function to get display label (dynamic or i18n)
  const getDisplayLabel = (item: {
    dynamicLabel?: string;
    i18nKey: string;
  }): string => {
    return item.dynamicLabel || t(item.i18nKey);
  };

  // Ana sayfadaysa breadcrumb gösterme
  if (!showBreadcrumb) {
    return null;
  }

  // Mobil için breadcrumb items'ları işle
  const getMobileBreadcrumbItems = () => {
    const items = breadcrumbItems.slice(1);

    // Mobilde sadece son 2 item'ı göster (Home + ... + Last 2)
    if (items.length <= 2) {
      return { visibleItems: items, hiddenItems: [] };
    }

    const lastTwoItems = items.slice(-2);
    const hiddenItems = items.slice(0, -2);

    return { visibleItems: lastTwoItems, hiddenItems };
  };

  // Uzun metinleri kısaltma fonksiyonu (sadece mobil için; şerit tam genişlik olduğu için 24)
  const truncateText = (text: string, maxLength: number = 24) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const { visibleItems, hiddenItems } = getMobileBreadcrumbItems();

  return (
    <div
      className="flex items-center gap-1 md:gap-4  min-w-0 max-w-full overflow-x-auto scrollbar-hide"
      aria-label="Breadcrumb"
    >
      {/* Home Icon */}
      <Link
        to="/"
        onClick={() => navigateToItem("/")}
        className="flex items-center justify-center size-5 text-text-quaternary-600 hover:text-text-secondary dark:text-gray-400 dark:hover:text-gray-300 transition-colors shrink-0"
      >
        <Home className="size-4" />
      </Link>

      {/* Hidden Items Dropdown (Mobile) */}
      {hiddenItems.length > 0 && (
        <>
          <ChevronRight className="size-4 md:size-5 text-text-quaternary-600 dark:text-gray-400 shrink-0" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-1 text-text-quaternary-600 hover:text-text-secondary dark:text-gray-400 dark:hover:text-gray-300 shrink-0"
              >
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {hiddenItems.map((item, index) => {
                const isClickable = item.path && item.path !== "";
                const displayLabel = getDisplayLabel(item);

                return (
                  <DropdownMenuItem
                    key={item.path || `hidden-${index}`}
                    asChild
                  >
                    {isClickable ? (
                      <Link
                        to={item.path}
                        onClick={() => navigateToItem(item.path)}
                        className="w-full text-left"
                      >
                        {displayLabel}
                      </Link>
                    ) : (
                      <span className="w-full text-left">{displayLabel}</span>
                    )}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}

      {/* Visible Breadcrumb Items */}
      {visibleItems.map((item, index) => {
        const isLast = index === visibleItems.length - 1;
        const isClickable = item.path && item.path !== "";
        const displayLabel = getDisplayLabel(item);

        return (
          <React.Fragment key={item.path || `item-${index}`}>
            {/* Separator */}
            <ChevronRight className="size-4 md:size-5 text-text-quaternary-600 dark:text-gray-400 shrink-0" />

            {/* Breadcrumb Item */}
            {isClickable && !isLast ? (
              <Link
                to={item.path}
                onClick={() => navigateToItem(item.path)}
                className="text-sm font-medium text-text-tertiary-600 hover:text-text-secondary dark:text-gray-400 dark:hover:text-gray-300 transition-colors truncate min-w-0 max-w-[40vw] md:max-w-none md:truncate-none"
                title={displayLabel} // Tooltip for full text
              >
                <span className="md:hidden">
                  {truncateText(displayLabel, 24)}
                </span>
                <span className="hidden md:inline">{displayLabel}</span>
              </Link>
            ) : (
              <span
                className={`text-sm font-medium truncate min-w-0 max-w-[40vw] md:max-w-none md:truncate-none ${
                  isLast
                    ? "text-module-accent"
                    : "text-text-tertiary-600 dark:text-gray-400"
                }`}
                title={displayLabel} // Tooltip for full text
              >
                <span className="md:hidden">
                  {truncateText(displayLabel, 24)}
                </span>
                <span className="hidden md:inline">{displayLabel}</span>
              </span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default Breadcrumb;
